import React, { useEffect, useState } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { ConfigProvider, theme as antdTheme } from 'antd'
import { Provider } from 'react-redux'
import { store } from './store/store'
import { useAppDispatch, useAppSelector } from './hooks/redux'
import { setUser } from './store/slices/authSlice'

// Components
import Layout from './components/Layout'
import LoginPage from './components/LoginPage'
import Dashboard from './components/Dashboard'
import LeadProcessing from './components/LeadProcessing'
import Statistics from './components/Statistics'
import Timeline from './components/Timeline'
import AdminPanel from './components/AdminPanel'
import Marketing from './components/Marketing'
import Analytics from './components/Analytics'
import ImportLeads from './components/ImportLeads'
import AdminRoute from './components/AdminRoute'

// Ant Design theme configuration
const theme = {
  token: {
    colorPrimary: '#1976d2',
    borderRadius: 0, // Set button border radius to 0
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
  components: {
    Button: {
      borderRadius: 0,
    },
  },
}

function AppContent() {
  const dispatch = useAppDispatch()
  const { isAuthenticated, loading, user } = useAppSelector((state) => state.auth)
  const [authCheckComplete, setAuthCheckComplete] = useState(false)

  useEffect(() => {
    // Check authentication status on mount
    const checkAuth = async () => {
      try {
        const response = await fetch('/api/v1/users/me/', {
          credentials: 'include',
        })
        if (response.ok) {
          const userData = await response.json()
          dispatch(setUser(userData))
        }
      } catch (error) {
        console.error('Auth check failed:', error)
      } finally {
        setAuthCheckComplete(true)
      }
    }
    checkAuth()
  }, [dispatch])

  // Show loading spinner until auth check is complete
  if (loading || !authCheckComplete) {
    return <div>Loading...</div>
  }

  return (
    <Routes>
      <Route path="/login" element={
        isAuthenticated ? <Navigate to="/dashboard" /> : <LoginPage />
      } />
      
      <Route path="/" element={
        isAuthenticated ? <Layout /> : <Navigate to="/login" />
      }>
        <Route index element={<Navigate to="/dashboard" />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="lead" element={<LeadProcessing />} />
        <Route path="lead/:leadId" element={<LeadProcessing />} />
        <Route path="stats" element={<Statistics />} />
        <Route path="timeline" element={<Timeline />} />
        <Route path="marketing" element={<Marketing />} />
        <Route path="analytics" element={<Analytics />} />
        <Route path="admin" element={<AdminPanel />} />
        <Route
          path="admin/import-leads"
          element={
            <AdminRoute user={user}>
              <ImportLeads />
            </AdminRoute>
          }
        />
      </Route>
      
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  )
}

function App() {
  return (
    <Provider store={store}>
      <ConfigProvider theme={theme}>
        <Router>
          <AppContent />
        </Router>
      </ConfigProvider>
    </Provider>
  )
}

export default App
